import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { ConversationBranchNavigation } from '../../state';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import useMessageActionButtonStyles from '../messageActionButton.styles';
import useStyles from './ConversationBranchControl.styles';

const BRANCH_ICON_SIZE_PX = 16;

/** Props for local conversation branch navigation. */
export type ConversationBranchControlProps = {
  navigation: ConversationBranchNavigation;
  disabled: boolean;
  onSwitch: (conversationId: string) => void;
};

/** Previous/next controls and position label for local query branches. */
function ConversationBranchControl({
  navigation,
  disabled,
  onSwitch,
}: ConversationBranchControlProps) {
  const { classes } = useStyles();
  const { classes: buttonClasses } = useMessageActionButtonStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const { current, total, previousConversationId, nextConversationId } =
    navigation;

  const handlePrevious = () => {
    if (previousConversationId !== null) {
      onSwitch(previousConversationId);
    }
  };
  const handleNext = () => {
    if (nextConversationId !== null) {
      onSwitch(nextConversationId);
    }
  };

  return (
    <div className={classes.root} data-testid="ai-sidekick-branch-control">
      <IconButton
        className={buttonClasses.button}
        onClick={handlePrevious}
        disabled={disabled || previousConversationId === null}
        aria-label={t('chat.branch.previousAriaLabel')}
        data-testid="ai-sidekick-branch-previous"
      >
        <ChevronLeftIcon sx={{ fontSize: BRANCH_ICON_SIZE_PX }} />
      </IconButton>
      <Typography
        component="span"
        variant="small_body_medium"
        className={classes.position}
        data-testid="ai-sidekick-branch-position"
      >
        {t('chat.branch.position', { current, total })}
      </Typography>
      <IconButton
        className={buttonClasses.button}
        onClick={handleNext}
        disabled={disabled || nextConversationId === null}
        aria-label={t('chat.branch.nextAriaLabel')}
        data-testid="ai-sidekick-branch-next"
      >
        <ChevronRightIcon sx={{ fontSize: BRANCH_ICON_SIZE_PX }} />
      </IconButton>
    </div>
  );
}

export default ConversationBranchControl;
